import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldWarning, ShieldCheck } from "@phosphor-icons/react";

// Dashboard early-warning: any WO that gained >10 ingested files in the last hour.
export default function GuardrailMonitorCard({ hours = 1, threshold = 10 }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const load = () => api.get("/admin/guardrail/file-spikes", { params: { hours, threshold } })
      .then(r => setData(r.data)).catch(() => setData({ spikes: [] }));
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [hours, threshold]);

  if (!data) return null;
  const spikes = data.spikes || [];
  if (spikes.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-200 rounded" data-testid="guardrail-ok">
        <ShieldCheck weight="fill" size={14}/> Upload guardrail OK — no WO gained more than {threshold} files in the last {hours}h
      </div>
    );
  }
  if (dismissed) return null;
  return (
    <div className="p-3 bg-red-50 border-2 border-red-600 rounded" data-testid="guardrail-alert">
      <div className="flex items-center gap-2">
        <ShieldWarning weight="fill" size={20} className="text-red-700"/>
        <div className="font-display font-black text-sm uppercase tracking-wider text-red-900">
          Upload spike — {spikes.length} WO{spikes.length === 1 ? "" : "s"} gained &gt;{threshold} files in the last {hours}h
        </div>
        <Link to="/admin/audit-wo" className="ml-auto text-[10px] font-black uppercase tracking-widest text-red-800 underline" data-testid="guardrail-audit-link">Routing Audit →</Link>
        <button onClick={() => setDismissed(true)} className="text-[10px] font-black uppercase tracking-widest text-slate-500" data-testid="guardrail-dismiss">Hide</button>
      </div>
      <div className="mt-2 space-y-1">
        {spikes.slice(0, 5).map(sp => (
          <Link key={sp.job_id} to={`/admin/jobs/${sp.job_id}`} data-testid={`guardrail-spike-${sp.job_id}`}
            className="flex items-center gap-2 text-xs bg-white border border-red-200 hover:border-red-600 rounded px-2 py-1.5">
            <span className="font-mono font-black bg-[#0f172a] text-white px-1.5 py-0.5 text-[10px]">{sp.work_order_number || "—"}</span>
            <span className="truncate flex-1">{sp.title || sp.property_address || "Untitled"}</span>
            <span className="font-black text-red-700">{sp.recent_files} files</span>
            {sp.fallback_files > 0 && <span className="text-[10px] font-black uppercase bg-amber-500 text-white px-1.5 py-0.5">{sp.fallback_files} fallback</span>}
            <span className="text-[10px] text-slate-500 hidden sm:inline">{sp.top_reason}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
